/**
 * The landing page: the position, what is coming, and what is to do.
 *
 * `/` redirected to the triage queue until 2026-09-17, which was honest and
 * unhelpful: it opened on the work rather than on the position. The forecast
 * is the better landing page, WITH ONE CONDITION, which is the reason the top
 * of this screen is shaped the way it is.
 *
 * THE FORECAST IS ONLY AS ACCURATE AS THE QUEUE IS WORKED
 *
 * Thirty uncategorized transactions means the books do not reflect reality,
 * and a confident number sitting on top of that is worse than no number: it is
 * a number the operator will act on, produced from a ledger that is missing a
 * month. So a sentence saying what the figures leave out sits ABOVE the money,
 * in both states, and the To Do list below carries the work itself.
 *
 * Until 2026-09-24 that sentence was a fold holding the breakdown and a link
 * for each queue. The To Do list now holds the queues as rows, with their
 * links, beside everything else to do; the operator asked for the two to be
 * merged. What stayed here is the claim, because it is about the figures and
 * has to be read before them.
 *
 * WHY THE NUMBERS ARE NOT RECOMPUTED HERE
 *
 * Every figure is a read that already exists -- `buildForecast` over the same
 * inputs the Cashflow screen uses, the receivables and payables totals, and
 * the To Do list the Home tab counts. A home screen with its own arithmetic
 * would be a second implementation of every number on it, and the first
 * disagreement would be between two screens that are both supposed to be this
 * business's position.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 * No charts, and no single "cash position" figure. The bank balance is not in
 * this application's books as a live number -- §6.6's reconciliation is how a
 * balance is proved -- and a headline figure derived from posted entries alone
 * would be confidently wrong by exactly the size of the queue.
 */

import { useAuth } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  api,
  type DepositObligation,
  type InvoiceRow,
  type OpenBill,
  type RecurringCommitment,
} from '../api.js';
import {
  CERTAINTIES,
  buildForecast,
  daysAfter,
  type CashflowResult,
  type Certainty,
} from '../lib/cashflow.js';
import { TodoSection } from '../components/TodoSection.js';
import { useTodo } from '../components/TodoProvider.js';
import { UndatedDeposits } from '../components/UndatedDeposits.js';
import { formatCents } from '../lib/money.js';

/**
 * How far ahead Home looks, and it is a CHOICE rather than a constant.
 *
 * Thirty days is the default: Home answers "what is this month", and the full
 * forecast is where a quarter is read. The operator asked for the longer
 * windows on 2026-09-17, and they cost nothing -- the horizon is a filter over
 * data already fetched, not a new read, which is the same argument the Cashflow
 * screen makes for its own control. The To Do list follows it too.
 */
const HORIZONS = [30, 60, 90, 180] as const;
type Horizon = (typeof HORIZONS)[number];
const DEFAULT_HORIZON: Horizon = 30;

function horizonLabel(days: Horizon): string {
  return days === 180 ? 'Next 6 Months' : `Next ${days} Days`;
}

/**
 * Which rows Upcoming Cash is showing.
 *
 * `out` and `in` are the two figures a forecast is actually made of, so the
 * cards that carry them filter to them. Overdue and bills owed are NOT filters
 * and are not rendered as such: both describe money OUTSIDE the window -- an
 * overdue invoice is behind it and a bill total is not dated at all -- so a
 * card that filtered the table to nothing would be answering a different
 * question with an empty list.
 */
type Shown = 'all' | 'out' | 'in';

const BAND_LABELS: Record<Certainty, string> = {
  known: 'Certain',
  due: 'Due On Terms',
  committed: 'Committed',
  projected: 'Projected',
};

/**
 * What was read, ONCE, before any horizon is applied.
 *
 * The forecast is derived from this rather than stored in it. Changing the
 * horizon must not refetch: the sources are read against each other on one
 * timeline, and a second read between two clicks would let a bill paid in
 * between appear on one view of the same screen and not another.
 */
interface Loaded {
  /** What the books owe on the payroll tax accounts, planned by the API. */
  deposits: DepositObligation[];
  bills: OpenBill[];
  invoices: InvoiceRow[];
  commitments: RecurringCommitment[];
  billsOwedCents: number;
  asOf: string;
}

export function HomePage() {
  const seesPayroll = useAuth().can('view_payroll');
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * The To Do list is the frame's, read once for the badge on the Home tab and
   * this screen together (TodoProvider). Recording a filing reloads it there,
   * so the count on the tab and the list here cannot disagree.
   */
  const { todo, error: todoError, reload } = useTodo();
  /*
   * Both in the URL, so a view of this screen is a thing that can be sent to
   * somebody or come back to. `replace` keeps the horizon out of the Back
   * history, which is the same decision the Reports tabs made.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const daysParam = Number(searchParams.get('days'));
  const horizon: Horizon = (HORIZONS as readonly number[]).includes(daysParam)
    ? (daysParam as Horizon)
    : DEFAULT_HORIZON;
  const shownParam = searchParams.get('show');
  const shown: Shown = shownParam === 'out' || shownParam === 'in' ? shownParam : 'all';
  /*
   * FOLDED TO START, asked for on 2026-09-24: the cards above say what the
   * window holds, and the rows are the detail. A card pressed opens it, since
   * pressing Leaving asks to see what is leaving -- and so does a link that
   * arrives already filtered.
   */
  const [cashOpen, setCashOpen] = useState(shown !== 'all');

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value === '') params.delete(key);
    else params.set(key, value);
    setSearchParams(params, { replace: true });
  }

  /** A card pressed: filter, and open what it filters. */
  function showOnly(value: '' | 'in' | 'out') {
    setParam('show', value);
    setCashOpen(true);
  }

  const load = useCallback(() => {
    Promise.all([
      // Payroll's deposits only for someone who may see payroll (0069): read
      // by anyone else they are refused, and that would empty the whole screen.
      seesPayroll ? api.getDepositTracker() : Promise.resolve({ tracker: { obligations: [] } } as unknown as Awaited<ReturnType<typeof api.getDepositTracker>>),
      api.listOpenBills({ limit: 200 }),
      api.listInvoices({ status: 'open', limit: 200 }),
      api.listCommitments(),
    ])
      .then(([deposits, bills, invoices, commitments]) => {
        setLoaded({
          deposits: deposits.tracker.obligations,
          bills: bills.bills,
          invoices: invoices.invoices,
          commitments: commitments.commitments,
          billsOwedCents: bills.totalOutstandingCents,
          asOf: bills.asOf,
        });
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not load the dashboard.'),
      );
  }, [seesPayroll]);

  useEffect(load, [load]);

  if (error !== null) {
    return (
      <section className="home">
        <h1>Home</h1>
        <p role="alert" className="error">
          {error}
        </p>
      </section>
    );
  }

  /*
   * Waits for the To Do list too, or for its failure: the sentence above the
   * figures is drawn from it, and figures shown before it would stand alone
   * for a moment, which is the one thing this screen is built not to do.
   */
  if (loaded === null || (todo === null && todoError === null)) {
    return (
      <section className="home">
        <h1>Home</h1>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  const from = loaded.asOf;
  const to = daysAfter(from, horizon);

  /*
   * The SAME composition the Cashflow screen runs, over the same inputs, with
   * a shorter window. Projected payroll is left out here -- pricing it needs
   * two more requests and a guess about the next pay date, and a month rarely
   * contains one. A payroll tax that is overdue IS in the window, at its start:
   * it leaves the day it is paid, which is now (`lib/cashflow.ts` says why).
   */
  const forecast: CashflowResult = buildForecast({
    from,
    to,
    deposits: loaded.deposits,
    bills: loaded.bills,
    invoices: loaded.invoices,
    commitments: loaded.commitments,
    projected: null,
  });

  const outflowCents = CERTAINTIES.reduce((sum, band) => sum + forecast.outflowCents[band], 0);
  const inflowCents = CERTAINTIES.reduce((sum, band) => sum + forecast.inflowCents[band], 0);
  /*
   * NET, and it is the one figure on this screen that adds the bands together.
   *
   * The forecast screen refuses to do that on purpose -- certain money and a
   * guess are different claims -- and the refusal holds there because that
   * screen projects a pay run nobody has made. Home projects NOTHING: every row
   * in this window is a statutory deadline, a recorded term or a recorded
   * commitment, so a single net figure is an answer rather than an average of
   * two different kinds of belief. The bands are still shown apart on each row.
   */
  const netCents = inflowCents - outflowCents;

  const overdueInvoiceCents = loaded.invoices
    .filter((invoice) => invoice.dueDate !== null && invoice.dueDate < from)
    .reduce((sum, invoice) => sum + invoice.outstandingCents, 0);

  const windowText = `${from} to ${to}, the ${horizonLabel(horizon).toLowerCase()}.`;

  const rows = forecast.items.filter((item) =>
    shown === 'all' ? true : shown === 'out' ? item.amountCents < 0 : item.amountCents > 0,
  );

  const queues = todo?.queues ?? null;
  const behind = queues === null ? null : queues.uncategorized + queues.unposted + queues.unmatched;

  return (
    <section className="home">
      <header className="row reports-head">
        <h1>Home</h1>
        {/*
          THE WINDOW IS SAID ON THE CONTROL THAT SETS IT, not beside the title.

          It read as a stray sentence in the header, which is what the operator
          reported: a date range floating next to "Home" belongs to nothing on
          the page. On the horizon control it is the answer to "what does Next
          90 Days actually mean", asked where the question arises.

          A `title` alone would put it out of reach of a keyboard and of a
          screen reader, so the same text is also the select's description. One
          string, `windowText`, so the tooltip and the description cannot drift.
        */}
        <label title={windowText}>
          Horizon{' '}
          <select
            aria-label="Horizon"
            aria-describedby="home-window"
            title={windowText}
            value={horizon}
            onChange={(event) => setParam('days', event.target.value)}
          >
            {HORIZONS.map((days) => (
              <option key={days} value={days}>
                {horizonLabel(days)}
              </option>
            ))}
          </select>
        </label>
        <span id="home-window" className="visually-hidden">
          {windowText}
        </span>
      </header>

      {/*
        WHAT THE FIGURES LEAVE OUT, ABOVE THEM, IN BOTH STATES.

        A line that appears only when there is bad news teaches the reader to
        skim past it, so "everything is categorized, posted and matched" is
        said in the same place and the same size as "31 uncategorized". The
        counts stay in the sentence, so the size of the gap is read before the
        figures are: "some transactions are not included" alone would read the
        same at three and at three hundred. The work itself is in To Do below.
      */}
      {behind === null ? (
        <div className="home-health" role="status">
          <p>
            <strong>Whether every transaction is in these figures could not be read.</strong> {todoError}
          </p>
        </div>
      ) : (
        <div className={`home-health${behind === 0 ? ' is-clear' : ''}`} role="status">
          {behind === 0 ? (
            <p>
              <strong>Everything is categorized, posted and matched.</strong> The figures below rest on the
              whole ledger.
            </p>
          ) : (
            <p>
              <strong>Some transactions are not in these figures:</strong>{' '}
              {[
                queues!.uncategorized > 0 ? `${queues!.uncategorized} uncategorized` : null,
                queues!.unposted > 0 ? `${queues!.unposted} not posted` : null,
                queues!.unmatched > 0 ? `${queues!.unmatched} unmatched` : null,
              ]
                .filter((part) => part !== null)
                .join(' · ')}
              . <span className="muted">They are on the To Do list below.</span>
            </p>
          )}
        </div>
      )}

      <UndatedDeposits deposits={forecast.undated} />

      {/*
        A HEADING AND A FIGURE, AND NOTHING ELSE.

        Each card carried a line of explanation underneath -- "show only these",
        "more in than out over this window", "bills outstanding — open the
        bills". The operator called them unhelpful on 2026-09-17 and they were:
        the pressed state is already visible, the sign on the figure already
        says which direction it runs, and a whole card that is a link does not
        need a caption telling you it is one.

        THREE OF THESE FILTER UPCOMING CASH AND TWO DO NOT, and the markup says
        which: the filters are <button>s and the other two are cards with a
        link in them. Overdue and bills owed describe money OUTSIDE this
        window -- an overdue invoice is behind it, a bill total is not dated at
        all -- so filtering the table by them would answer a different question
        with an empty list. They lead to the screen that does answer it.
      */}
      <div className="row home-figures">
        <button
          type="button"
          className={`home-figure is-filter${shown === 'in' ? ' is-current' : ''}`}
          aria-pressed={shown === 'in'}
          onClick={() => showOnly(shown === 'in' ? '' : 'in')}
        >
          <h2>Arriving</h2>
          <p className="cashflow-amount">
            <span className="num">{formatCents(inflowCents)}</span>
          </p>
        </button>
        <button
          type="button"
          className={`home-figure is-filter${shown === 'out' ? ' is-current' : ''}`}
          aria-pressed={shown === 'out'}
          onClick={() => showOnly(shown === 'out' ? '' : 'out')}
        >
          <h2>Leaving</h2>
          <p className="cashflow-amount">
            <span className="num">{formatCents(-outflowCents)}</span>
          </p>
        </button>
        <button
          type="button"
          className={`home-figure is-filter is-net${shown === 'all' ? ' is-current' : ''}`}
          aria-pressed={shown === 'all'}
          onClick={() => showOnly('')}
        >
          <h2>Net</h2>
          <p className="cashflow-amount">
            <span className="num">{formatCents(netCents)}</span>
          </p>
        </button>
        {/*
          THE WHOLE CARD IS THE TARGET, asked for on 2026-09-17. It was a card
          with a small link inside it, which made the number -- the thing being
          pointed at -- the one part you could not click.
        */}
        <Link to="/invoices?status=open" className="home-figure is-link">
          <h2>Overdue To You</h2>
          <p className="cashflow-amount">
            <span className="num">{formatCents(overdueInvoiceCents)}</span>
          </p>
        </Link>
        <Link to="/bills" className="home-figure is-link">
          <h2>Owed By You</h2>
          <p className="cashflow-amount">
            <span className="num">{formatCents(-loaded.billsOwedCents)}</span>
          </p>
        </Link>
      </div>

      {/*
        UPCOMING CASH, renamed from "What Is Coming" on 2026-09-24: with a To
        Do list beside it, "coming" no longer said it was about money. The
        summary carries the count and the filter, so what is folded away is the
        rows and nothing that is said about them.
      */}
      <section className="home-upcoming" aria-label="Upcoming Cash">
        <button
          type="button"
          className="home-health-toggle"
          aria-expanded={cashOpen}
          onClick={() => setCashOpen((open) => !open)}
        >
          <span>
            <strong>Upcoming Cash</strong>{' '}
            <span className="muted">
              {forecast.items.length === 0
                ? `Nothing falls in the ${horizonLabel(horizon).toLowerCase()}`
                : `${forecast.items.length} ${forecast.items.length === 1 ? 'payment' : 'payments'} in the ${horizonLabel(horizon).toLowerCase()}`}
              {shown !== 'all' && (shown === 'out' ? ' · Leaving only' : ' · Arriving only')}
            </span>
          </span>
          <span className="home-health-chevron" aria-hidden="true">
            {cashOpen ? '▲' : '▼'}
          </span>
        </button>

        {cashOpen &&
          (forecast.items.length === 0 ? (
            <p className="muted">
              Nothing falls in the {horizonLabel(horizon).toLowerCase()}.{' '}
              <Link to="/reports/cashflow">The forecast</Link> looks further out.
            </p>
          ) : (
            <>
              {shown !== 'all' && (
                <p className="muted home-cash-filter">
                  {shown === 'out' ? 'Leaving only' : 'Arriving only'} —{' '}
                  <button type="button" className="link" onClick={() => setParam('show', '')}>
                    show everything
                  </button>
                </p>
              )}
              <div className="table-scroll">
                <table className="lines cashflow-list">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>What</th>
                      <th>Certainty</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => (
                      <tr key={`${item.date}-${item.source}-${item.link?.id ?? item.label}`}>
                        <td className="date-cell">{item.date}</td>
                        <td>{item.label}</td>
                        <td>
                          <span className={`tag tag-${item.certainty}`}>{BAND_LABELS[item.certainty]}</span>
                        </td>
                        <td className="num">{formatCents(item.amountCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 && (
                /*
                  A filter emptying the table is not the same as an empty
                  window, and the sentence above only covers the second.
                  Without this the screen shows a header over nothing and looks
                  broken.
                */
                <p className="muted">
                  Nothing is {shown === 'out' ? 'leaving' : 'arriving'} in this window.{' '}
                  <button type="button" className="link" onClick={() => setParam('show', '')}>
                    Show everything
                  </button>
                  .
                </p>
              )}
              <p className="muted">
                Certain, due and committed money are different claims and are not added together here.{' '}
                <Link to="/reports/cashflow">The full forecast</Link> separates them, projects the next
                pay run, and takes a longer horizon.
              </p>
            </>
          ))}
      </section>

      {todo === null ? (
        <section className="todo" aria-labelledby="todo-heading">
          <h2 id="todo-heading">To Do</h2>
          <p role="alert" className="error">
            {todoError}
          </p>
        </section>
      ) : (
        <TodoSection todo={todo} horizon={horizon} horizonLabel={horizonLabel(horizon)} reload={reload} />
      )}
    </section>
  );
}
